import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import type { User, LoginCredentials, AuthContextType } from '../types';
import { authService } from '../services/api';

// Auth state and actions
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Auth reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
      
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
      
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
      
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
      
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
      
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth();
  }, []);

  // Token refresh interval — fire at 80% of the 15-min access token lifetime
  // so the token is refreshed well before it expires.
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (state.isAuthenticated) {
      if (!refreshIntervalRef.current) {
        refreshIntervalRef.current = setInterval(() => {
          refreshToken();
        }, 12 * 60 * 1000); // 12 minutes (80% of 15-min token)
      }
    } else {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [state.isAuthenticated]);

  const initializeAuth = async () => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const token = localStorage.getItem('auth_token');
      if (!token) {
        dispatch({ type: 'AUTH_ERROR', payload: 'No token found' });
        return;
      }

      // Get current user with existing token
      const response = await authService.getCurrentUser();
      
      if (response.success && response.data) {
        const userData = response.data;
        // Log roles for debugging
        console.log('InitializeAuth - User roles:', userData.roles || [userData.role]);
        
        // Ensure roles array exists for all users
        userData.roles = userData.roles || [userData.role || 'user'];
        
        dispatch({ type: 'AUTH_SUCCESS', payload: userData });
      } else {
        // Token might be invalid
        localStorage.removeItem('auth_token');
        dispatch({ type: 'AUTH_ERROR', payload: 'Invalid token' });
      }
    } catch (error) {
      localStorage.removeItem('auth_token');
      dispatch({ type: 'AUTH_ERROR', payload: 'Failed to initialize authentication' });
    }
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      const response = await authService.login(credentials);
      console.log('Login response:', response);
      
      if (response.success && response.data) {
        const { user, token } = response.data;
        localStorage.setItem('auth_token', token);
        
        // Log roles for debugging
        console.log('User logged in with roles:', user.roles || [user.role]);
        
        // Ensure roles array exists for all users
        user.roles = user.roles || [user.role || 'user'];
        
        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      console.error('Login error:', message);
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const register = async (data: { email: string; password: string; first_name: string; last_name: string; invite_token?: string; org_id?: string; captchaToken?: string; locale?: string }) => {
    try {
      dispatch({ type: 'AUTH_START' });

      const response = await authService.register(data);

      if (response.success && response.data) {
        const { user, token } = response.data;
        localStorage.setItem('auth_token', token);

        user.roles = user.roles || [user.role || 'user'];

        dispatch({ type: 'AUTH_SUCCESS', payload: user });
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'AUTH_ERROR', payload: message });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const refreshToken = async () => {
    const MAX_RETRIES = 2;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const response = await authService.refreshToken();

        if (response.success && response.data) {
          localStorage.setItem('auth_token', response.data.token);
          // Get updated user data
          const userResponse = await authService.getCurrentUser();
          if (userResponse.success && userResponse.data) {
            dispatch({ type: 'AUTH_SUCCESS', payload: userResponse.data });
          }
          return; // success — exit
        }
      } catch (error) {
        console.error(`Token refresh attempt ${attempt + 1} failed:`, error);

        if (attempt < MAX_RETRIES) {
          // Wait before retrying (1s, then 2s)
          await new Promise((r) => setTimeout(r, (attempt + 1) * 1000));
        }
      }
    }

    // All retries exhausted — log out
    console.error('Token refresh failed after all retries, logging out');
    logout();
  };

  const refreshProfile = async (): Promise<User | null> => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;

      const response = await authService.getCurrentUser();
      if (response.success && response.data) {
        const userData = response.data;
        userData.roles = userData.roles || [userData.role || 'user'];
        dispatch({ type: 'AUTH_SUCCESS', payload: userData });
        return userData;
      }
      return null;
    } catch {
      return null;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const contextValue: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    login,
    register,
    logout,
    refreshToken,
    refreshProfile,
    clearError
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher-order component for protected routes
export const withAuth = <P extends object>(Component: React.ComponentType<P>) => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // In a real app, this would redirect to login
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
            <p className="text-gray-600">Please log in to access this page.</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};